import React, { useState } from 'react';
import { User, Bot, Copy, Check } from 'lucide-react';
import CitationBlock from '../../common/components/chat/CitationBlock';
import Typewriter from '../../common/components/chat/Typewriter';
import ConsultationFooter from '../../common/components/chat/ConsultationFooter';

/**
 * 개별 채팅 메시지(사용자/AI)를 렌더링하는 컴포넌트.
 */
const ChatMessage = ({ msg, isLastAiMessage, completedTypingIds, handleComplete, scrollToBottom }) => {
    const isTypingDone = completedTypingIds.has(msg.id) || !isLastAiMessage;
    const [isCopied, setIsCopied] = useState(false);

    const formatTime = (ts) => {
        const d = new Date(ts);
        const today = new Date();
        const isToday = d.toDateString() === today.toDateString();
        const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
        return isToday ? time : `${d.getMonth() + 1}/${d.getDate()} ${time}`;
    };

    const handleCopy = async () => {
        try {
            // <br> 태그를 실제 줄바꿈 문자로 변경 후 HTML 태그 제거
            const textWithNewlines = msg.text.replace(/<br\s*[/]?>/gi, '\n');
            const tmp = document.createElement('div');
            tmp.innerHTML = textWithNewlines;
            const cleanText = (tmp.textContent || tmp.innerText || '').trim();

            await navigator.clipboard.writeText(cleanText);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
            console.error('복사 실패:', err);
        }
    };

    return (
        <div
            className={`flex gap-3 items-start animate-fade-in-up ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
        >
            <div
                className={`rounded-full flex items-center justify-center shadow-sm shrink-0 ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-white'}`}
                style={{ width: '38px', height: '38px' }}
            >
                {msg.role === 'user' ? <User size={19} /> : <Bot size={19} />}
            </div>
            <div className={`flex flex-col max-w-[85%] sm:max-w-[75%] ${msg.role === 'user' ? 'items-end' : ''}`}>
                <div className={`flex items-end gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                    {/* 말풍선 */}
                    <div className={`py-2.5 px-4 rounded-3xl text-base leading-[1.6] tracking-[-0.01em] shadow-sm w-fit transition-all duration-300 ${msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-tr-sm shadow-blue-600/20'
                        : 'bg-white text-gray-800 rounded-tl-sm border border-gray-100 ring-1 ring-gray-900/5'
                        }`}>
                        {isLastAiMessage && !completedTypingIds.has(msg.id) ? (
                            <Typewriter
                                text={msg.text}
                                onComplete={() => handleComplete(msg.id)}
                                onProgress={() => scrollToBottom('auto')}
                            />
                        ) : (
                            <span dangerouslySetInnerHTML={{ __html: msg.text }} />
                        )}
                        {msg.role === 'ai' && msg.meta && isTypingDone && (
                            <>
                                <CitationBlock
                                    citations={msg.citations}
                                />
                                <ConsultationFooter />
                            </>
                        )}
                    </div>

                    {/* 시간 표시 (말풍선 옆 바닥쪽) */}
                    <span className="text-xs text-gray-400 mb-1 flex-shrink-0 select-none">
                        {formatTime(msg.timestamp)}
                    </span>
                </div>

                {/* 하단 보조 정보 (복사 버튼 등) */}
                {msg.role === 'ai' && isTypingDone && (
                    <div className="mt-1.5 px-2 select-none">
                        <button
                            onClick={handleCopy}
                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors focus:outline-none"
                            title="답변 복사하기"
                        >
                            {isCopied ? (
                                <>
                                    <Check size={12} className="text-green-500" />
                                    <span className="text-green-500 font-medium tracking-tight">복사됨</span>
                                </>
                            ) : (
                                <>
                                    <Copy size={12} />
                                    <span className="tracking-tight">복사</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatMessage;
